import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/components/auth-context'
import type { Project } from '@/types/project'

export interface ProjectWithStats extends Project {
    stats: {
        totalTasks: number
        completedTasks: number
        progress: number
        totalEstimatedHours: number
        totalWorkedHours: number
        firstTaskId?: string
    }
}

export const projectKeys = {
    all: ['projects'] as const,
    list: (orgId: string | undefined) => [...projectKeys.all, 'list', orgId] as const,
    trash: (orgId: string | undefined) => [...projectKeys.all, 'trash', orgId] as const,
    notes: (projectId: string) => [...projectKeys.all, 'notes', projectId] as const,
}

// 1. Query para obtener y calcular stats (idéntico al context original)
export function useProjectsQuery() {
    const { organization, user } = useAuth()

    return useQuery({
        queryKey: projectKeys.list(organization?.id),
        queryFn: async () => {
            if (!organization || !user) return []

            const { data, error } = await supabase
                .from('projects')
                .select('*, tasks(id, status, estimated_hours), time_entries(total_hours)')
                .eq('organization_id', organization.id)
                .is('deleted_at', null)
                .order('name', { ascending: true })

            if (error) throw error

            if (data) {
                type TaskRow = { id: string; status: string; estimated_hours: number | null }
                type TimeEntryRow = { total_hours: number | null; status: string; clock_in: string }
                type ProjectRow = Project & { tasks?: TaskRow[]; time_entries?: TimeEntryRow[] }

                return data.map((p: ProjectRow) => {
                    const tasks = p.tasks || []
                    const timeEntries = p.time_entries || []

                    const totalTasks = tasks.length
                    const completedTasks = tasks.filter((t) => t.status === 'completed').length
                    const totalEstimatedHours = tasks.reduce((acc, t) => acc + (t.estimated_hours || 0), 0)
                    const totalWorkedHours = timeEntries.reduce((acc, te) => {
                        if (te.total_hours) return acc + te.total_hours
                        if (te.status === 'active' || te.status === 'paused') {
                            const start = new Date(te.clock_in)
                            const diff = (new Date().getTime() - start.getTime()) / 3600000
                            return acc + Math.max(0, diff)
                        }
                        return acc
                    }, 0)

                    let progress = 0
                    if (totalEstimatedHours > 0) {
                        progress = Math.min(Math.round((totalWorkedHours / totalEstimatedHours) * 100), 100)
                    } else if (totalTasks > 0) {
                        progress = Math.round((completedTasks / totalTasks) * 100)
                    }

                    const firstTaskId = tasks.find((t) => t.status !== 'completed')?.id || tasks[0]?.id
                    const { tasks: _discardedTasks, time_entries: _discardedTimeEntries, ...projectData } = p

                    return {
                        ...projectData,
                        stats: {
                            totalTasks,
                            completedTasks,
                            progress,
                            totalEstimatedHours,
                            totalWorkedHours,
                            firstTaskId
                        }
                    } as ProjectWithStats
                })
            }
            return []
        },
        enabled: !!organization && !!user, // Solo se ejecuta si hay sesión
    })
}

// Query exclusiva para la papelera (proyectos con deleted_at NOT NULL)
export function useTrashProjectsQuery() {
    const { organization, user } = useAuth()

    return useQuery({
        queryKey: projectKeys.trash(organization?.id),
        queryFn: async () => {
            if (!organization || !user) return []

            const { data, error } = await supabase
                .from('projects')
                .select('*, tasks(id, status, estimated_hours), time_entries(total_hours)')
                .eq('organization_id', organization.id)
                .not('deleted_at', 'is', null)
                .order('deleted_at', { ascending: false })

            if (error) throw error
            return (data || []) as ProjectWithStats[]
        },
        enabled: !!organization && !!user,
    })
}

export function useCreateProjectMutation() {
    const queryClient = useQueryClient()
    const { organization } = useAuth()

    return useMutation({
        mutationFn: async ({ name, color = '#3b82f6', hourlyRate = 0, clientId }: { name: string, color?: string, hourlyRate?: number, clientId?: string | null }) => {
            if (!organization) throw new Error("No organization found")

            const { data, error } = await supabase
                .from('projects')
                .insert({
                    organization_id: organization.id,
                    client_id: clientId || null,
                    name,
                    color,
                    hourly_rate: hourlyRate,
                    status: 'active'
                })
                .select()
                .single()

            if (error) throw error
            return data as Project
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.list(organization?.id) })
        },
    })
}

export function useUpdateProjectMutation() {
    const queryClient = useQueryClient()
    const { organization } = useAuth()

    return useMutation({
        mutationFn: async ({ id, updates }: { id: string, updates: Partial<Project> }) => {
            const { data, error } = await supabase
                .from('projects')
                .update(updates)
                .eq('id', id)
                .select()
                .single()

            if (error) throw error
            return data as Project
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.list(organization?.id) })
        },
    })
}

export function useDeleteProjectMutation() {
    const queryClient = useQueryClient()
    const { organization } = useAuth()

    return useMutation({
        mutationFn: async ({ id, softDelete = true }: { id: string, softDelete?: boolean }) => {
            if (softDelete) {
                const now = new Date().toISOString()
                const { error } = await supabase.from('projects').update({ deleted_at: now }).eq('id', id)
                if (error) throw error

                // Cerrar cualquier sesión activa o pausada asociada a este proyecto
                await supabase
                    .from('time_entries')
                    .update({ clock_out: now, status: 'completed', total_hours: 0 })
                    .eq('project_id', id)
                    .in('status', ['active', 'paused'])
            } else {
                // Hard delete: primero cerrar sesiones activas, luego borrar
                const now = new Date().toISOString()
                await supabase
                    .from('time_entries')
                    .update({ clock_out: now, status: 'completed', total_hours: 0 })
                    .eq('project_id', id)
                    .in('status', ['active', 'paused'])
                const { error } = await supabase.from('projects').delete().eq('id', id)
                if (error) throw error
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.list(organization?.id) })
            queryClient.invalidateQueries({ queryKey: projectKeys.trash(organization?.id) })
            queryClient.invalidateQueries({ queryKey: ['time-entries'] }) // Refrescar el timer activo
        },
    })
}

export function useRestoreProjectMutation() {
    const queryClient = useQueryClient()
    const { organization } = useAuth()

    return useMutation({
        mutationFn: async (projectId: string) => {
            const { error } = await supabase.from('projects').update({ deleted_at: null }).eq('id', projectId)
            if (error) throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.list(organization?.id) })
            queryClient.invalidateQueries({ queryKey: projectKeys.trash(organization?.id) })
        },
    })
}

// 3. Project Notes Queries and Mutations
export function useProjectNotesQuery(projectId: string) {
    return useQuery({
        queryKey: projectKeys.notes(projectId),
        queryFn: async () => {
            if (!projectId) return []
            const { data, error } = await supabase
                .from('project_notes')
                .select('*')
                .eq('project_id', projectId)
                .order('created_at', { ascending: false })

            if (error) {
                console.error("Error fetching project notes:", error)
                throw error
            }
            return data || []
        },
        enabled: !!projectId,
    })
}

export function useUpsertProjectNoteMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ projectId, content, noteId }: { projectId: string, content: string, noteId?: string }) => {
            if (noteId) {
                const { data, error } = await supabase
                    .from('project_notes')
                    .update({ content })
                    .eq('id', noteId)
                    .select()
                    .single()

                if (error) throw error
                return data
            } else {
                const { data, error } = await supabase
                    .from('project_notes')
                    .insert({ project_id: projectId, content })
                    .select()
                    .single()

                if (error) throw error
                return data
            }
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: projectKeys.notes(variables.projectId) })
        },
    })
}

export function useDeleteProjectNoteMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ noteId }: { noteId: string, projectId: string }) => {
            const { error } = await supabase
                .from('project_notes')
                .delete()
                .eq('id', noteId)

            if (error) throw error
        },
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: projectKeys.notes(projectId) })
        },
    })
}
